import {
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Min,
  Max,
  MaxLength,
} from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Transform } from 'class-transformer'
import { PedagogicContentState } from '../../../enums/content.enum'
import {
  MAX_TEACHING_DESCRIPTION_LENGTH,
  MIN_WORKLOAD,
  MAX_WORKLOAD,
  MIN_WEIGHT,
  MAX_WEIGHT,
} from '../../../constants/teaching.constants'

const toNumber = ({ value }: { value: any }) => {
  if (value === '' || value === null || value === undefined) return undefined
  return Number(value)
}

const toStringOrUndefined = ({ value }: { value: any }) => {
  if (value === '' || value === null) return undefined
  return value
}

export class CreateTeachingDto {
  @ApiProperty({
    description: 'ID de la matière',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty({ message: 'La matière est requise' })
  @IsUUID('4')
  subjectExternalId!: string

  @ApiPropertyOptional({
    description:
      'ID du niveau pédagogique (optionnel si déductible de la classe)',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  levelExternalId?: string

  @ApiPropertyOptional({
    description: 'ID de la classe',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @Transform(toStringOrUndefined)
  @IsUUID('4')
  classId?: string

  @ApiPropertyOptional({
    description: 'ID du groupe',
    example: '660e8400-e29b-41d4-a716-446655440001',
    type: 'string',
  })
  @IsOptional()
  @Transform(toStringOrUndefined)
  @IsUUID('4')
  groupId?: string

  @ApiPropertyOptional({
    description: "ID de l'établissement",
    example: '660e8400-e29b-41d4-a716-446655440001',
    type: 'string',
  })
  @IsOptional()
  @Transform(toStringOrUndefined)
  @IsUUID('4')
  establishmentId?: string

  @ApiPropertyOptional({
    description: "État de l'enseignement",
    enum: PedagogicContentState,
    default: PedagogicContentState.ACTIVE,
  })
  @IsOptional()
  @IsEnum(PedagogicContentState)
  state?: PedagogicContentState

  @ApiProperty({
    description: 'ID de la version de programme (curriculum)',
    example: '770e8400-e29b-41d4-a716-446655440002',
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le programme est requis' })
  @IsUUID('4')
  programVersionId!: string

  @ApiProperty({
    description: "ID de l'année scolaire",
    example: '550e8400-e29b-41d4-a716-446655440002',
    type: 'string',
  })
  @IsNotEmpty({ message: "L'année scolaire est requise" })
  @IsUUID('4')
  schoolYearId!: string

  @ApiPropertyOptional({
    description: 'Description',
    maxLength: MAX_TEACHING_DESCRIPTION_LENGTH,
    type: 'string',
  })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_TEACHING_DESCRIPTION_LENGTH)
  description?: string

  @ApiPropertyOptional({
    description: 'Poids dans la progression',
    minimum: MIN_WEIGHT,
    maximum: MAX_WEIGHT,
    type: Number,
  })
  @IsOptional()
  @Transform(toNumber)
  @IsNumber()
  @Min(MIN_WEIGHT)
  @Max(MAX_WEIGHT)
  weightInProgression?: number

  @ApiPropertyOptional({
    description: 'Charge de travail estimée (heures)',
    minimum: MIN_WORKLOAD,
    maximum: MAX_WORKLOAD,
    type: Number,
  })
  @IsOptional()
  @Transform(toNumber)
  @IsNumber()
  @Min(MIN_WORKLOAD)
  @Max(MAX_WORKLOAD)
  estimatedWorkload?: number
}
